import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RadioGroup } from '@headlessui/react';
import { CheckCircleIcon, ChartPieIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useData } from '../../context/DataContext';
import { formatCurrency, getCurrencySymbol } from '../../utils/formatters';
import { convertCurrency } from '../../utils/calculations';
import { PieChart, Pie, Cell as RechartsCell, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { FINANCIAL_COLORS } from '../../utils/colors';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import { getGoalIcon } from '../../utils/goalIcons';

const Allocations = () => {
  const { portfolios, goals, updateGoal, exchangeRates } = useData();
  const navigate = useNavigate();
  const [allocations, setAllocations] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [chartType, setChartType] = useState('pie');
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('table'); // 'table' or 'charts'

  // Warn about unsaved changes
  useUnsavedChanges(hasChanges, 'You have unsaved allocation changes. Are you sure you want to leave?');

  // Initialize allocations from goals
  useEffect(() => {
    const initialAllocations = {};
    goals.forEach(goal => {
      if (goal.allocations) {
        goal.allocations.forEach(alloc => {
          const key = `${goal.id}|||${alloc.portfolioId}`;
          // Normalize the type - default to 'percentage' if not set or invalid
          const allocType = alloc.type === 'amount' ? 'amount' : 'percentage';
          initialAllocations[key] = {
            type: allocType,
            value: alloc.value || alloc.percentage || 0,
          };
        });
      }
    });
    setAllocations(initialAllocations);
  }, [goals]);

  const handleAllocationChange = (goalId, portfolioId, field, value) => {
    const key = `${goalId}|||${portfolioId}`;
    setAllocations(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value,
      },
    }));
    setHasChanges(true);
  };

  const handleSaveAllocations = async () => {
    setIsSaving(true);

    try {
      // Validate allocations before saving
      const portfolioTotals = {};

      // Calculate totals for each portfolio
      Object.entries(allocations).forEach(([key, alloc]) => {
        const [, portfolioId] = key.split('|||');
        if (!portfolioTotals[portfolioId]) {
          portfolioTotals[portfolioId] = { percentage: 0, amount: 0 };
        }

        if (alloc.value > 0) {
          if (!alloc.type || alloc.type === 'percentage') {
            portfolioTotals[portfolioId].percentage += alloc.value || 0;
          } else if (alloc.type === 'amount') {
            portfolioTotals[portfolioId].amount += alloc.value || 0;
          }
        }
      });

      // Validate each portfolio
      for (const [portfolioId, totals] of Object.entries(portfolioTotals)) {
        const portfolio = portfolios.find(p => p.id === portfolioId);
        if (!portfolio) continue;

        if (totals.percentage > 100) {
          toast.error(`${portfolio.name}: Total percentage allocation (${totals.percentage.toFixed(1)}%) exceeds 100%`);
          setIsSaving(false);
          return;
        }

        if (totals.amount > portfolio.currentValue) {
          toast.error(`${portfolio.name}: Total fixed amount allocation exceeds portfolio value`);
          setIsSaving(false);
          return;
        }
      }

      // Group allocations by goal
      const goalAllocations = {};

      Object.entries(allocations).forEach(([key, alloc]) => {
        const [goalId, portfolioId] = key.split('|||');
        if (!goalAllocations[goalId]) {
          goalAllocations[goalId] = [];
        }

        if (alloc.value > 0) {
          goalAllocations[goalId].push({
            portfolioId,
            type: alloc.type,
            value: alloc.value,
          });
        }
      });

      // Update each goal
      goals.forEach(goal => {
        const newAllocations = goalAllocations[goal.id] || [];
        updateGoal(goal.id, { allocations: newAllocations });
      });

      setHasChanges(false);
      toast.success('Allocations saved successfully');
    } catch (error) {
      toast.error('Failed to save allocations');
    } finally {
      setIsSaving(false);
    }
  };

  const getAllocation = (goalId, portfolioId) => {
    const key = `${goalId}|||${portfolioId}`;
    return allocations[key] || { type: 'percentage', value: 0 };
  };

  const calculateAllocatedValue = (portfolio, allocation) => {
    // Treat as percentage if type is 'percentage' or undefined/empty (default)
    if (!allocation.type || allocation.type === 'percentage') {
      return portfolio.currentValue * (allocation.value / 100);
    } else {
      return allocation.value;
    }
  };

  const calculateTotalAllocated = (portfolioId) => {
    let totalPercentage = 0;
    let totalAmount = 0;

    goals.forEach(goal => {
      const alloc = getAllocation(goal.id, portfolioId);
      // Treat as percentage if type is 'percentage' or undefined/empty (default)
      if (!alloc.type || alloc.type === 'percentage') {
        totalPercentage += alloc.value || 0;
      } else if (alloc.type === 'amount') {
        totalAmount += alloc.value || 0;
      }
    });

    const portfolio = portfolios.find(p => p.id === portfolioId);
    const percentageValue = portfolio ? (portfolio.currentValue * totalPercentage / 100) : 0;

    return {
      totalPercentage,
      totalAmount,
      totalValue: percentageValue + totalAmount,
    };
  };

  // Prepare chart data for each portfolio
  const getChartDataForPortfolio = (portfolioId) => {
    const portfolio = portfolios.find(p => p.id === portfolioId);
    if (!portfolio) return [];

    const data = [];
    goals.forEach(goal => {
      const alloc = getAllocation(goal.id, portfolioId);
      if (alloc.value > 0) {
        const allocatedValue = calculateAllocatedValue(portfolio, alloc);
        const convertedValue = convertCurrency(
          allocatedValue,
          portfolio.currency,
          portfolio.currency,
          exchangeRates
        );
        data.push({
          name: goal.name,
          value: convertedValue,
          color: goal.color,
        });
      }
    });

    // Add unallocated portion
    const totals = calculateTotalAllocated(portfolioId);
    const remaining = portfolio.currentValue - totals.totalValue;
    if (remaining > 0) {
      data.push({
        name: 'Unallocated',
        value: remaining,
        color: '#d9d9d9',
      });
    }

    return data;
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-row justify-between items-center mb-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold text-gray-900 m-0">Goal Allocations</h1>
          <p className="text-sm text-gray-600">
            Allocate your portfolio accounts to financial goals
          </p>
        </div>
        <button
          onClick={handleSaveAllocations}
          disabled={!hasChanges || isSaving}
          className="flex items-center gap-2 px-4 py-2  text-white rounded-lg  transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
          style={{ backgroundColor: '#012E58' }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#01234a'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#012E58'}
        >
          {isSaving ? (
            <>
              <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Saving...</span>
            </>
          ) : (
            <>
              <CheckCircleIcon className="w-5 h-5" />
              <span>Save Allocations</span>
            </>
          )}
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex gap-6">
          <button
            onClick={() => setActiveTab('table')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'table'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Allocation Table
          </button>
          <button
            onClick={() => setActiveTab('charts')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'charts'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Allocation Charts
          </button>
        </div>
      </div>

      {goals.length === 0 || portfolios.length === 0 ? (
        <div className="bg-gray-50 rounded-lg border border-gray-200 p-8 text-center">
          <p className="text-gray-700">
            {goals.length === 0
              ? 'No goals created yet. Create a goal first to start allocating.'
              : 'No accounts created yet. Create an account first to start allocating.'}
          </p>
        </div>
      ) : (
        <>
          {/* Allocation Table */}
          {activeTab === 'table' && (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider sticky left-0 bg-gray-50 border-r border-gray-200">
                      Account
                    </th>
                    {goals.map(goal => {
                      const GoalIcon = getGoalIcon(goal.icon || goal.emoji);
                      return (
                        <th key={goal.id} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[180px]">
                          <div className="flex flex-col gap-1">
                            <GoalIcon className="w-6 h-6 text-blue-600" />
                            <div className="text-sm font-semibold text-gray-900">{goal.name}</div>
                            <div className="text-xs text-gray-500 font-normal">
                              Target: {formatCurrency(goal.targetAmount, goal.currency, 0)}
                            </div>
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {portfolios.map((portfolio) => {
                    const totals = calculateTotalAllocated(portfolio.id);
                    const remaining = portfolio.currentValue - totals.totalValue;

                    return (
                      <tr key={portfolio.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 sticky left-0 bg-white border-r border-gray-200">
                          <div className="flex flex-col gap-1">
                            <div className="text-sm font-semibold text-gray-900">{portfolio.name}</div>
                            <div className="text-xs text-gray-600">
                              {formatCurrency(portfolio.currentValue, portfolio.currency, 0)}
                            </div>
                            <div className={`text-xs ${remaining < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                              <span className="font-bold">Unallocated:</span> {formatCurrency(remaining, portfolio.currency, 0)}
                            </div>
                          </div>
                        </td>
                        {goals.map(goal => {
                          const allocation = getAllocation(goal.id, portfolio.id);
                          const allocatedValue = calculateAllocatedValue(portfolio, allocation);
                          const convertedValue = convertCurrency(
                            allocatedValue,
                            portfolio.currency,
                            goal.currency,
                            exchangeRates
                          );

                          return (
                            <td key={goal.id} className="px-4 py-3">
                              <div className="flex flex-col gap-2">
                                <div className="flex gap-2">
                                  <select
                                    value={allocation.type}
                                    onChange={(e) => handleAllocationChange(goal.id, portfolio.id, 'type', e.target.value)}
                                    className="text-xs px-2 py-1 border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  >
                                    <option value="percentage">%</option>
                                    <option value="amount">{getCurrencySymbol(portfolio.currency)}</option>
                                  </select>
                                  <input
                                    type="number"
                                    value={allocation.value}
                                    onChange={(e) => handleAllocationChange(goal.id, portfolio.id, 'value', parseFloat(e.target.value) || 0)}
                                    min={0}
                                    max={allocation.type === 'percentage' ? 100 : portfolio.currentValue}
                                    step={allocation.type === 'percentage' ? 0.1 : 1}
                                    className="text-xs px-2 py-1 border border-gray-300 rounded w-20 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  />
                                </div>
                                {allocation.value > 0 && (
                                  <div className="text-xs text-gray-500">
                                    ≈ {formatCurrency(convertedValue, goal.currency, 0)}
                                  </div>
                                )}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          )}

          {/* Allocation Visualizations */}
          {activeTab === 'charts' && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
          <div className="flex flex-row justify-between items-center mb-6">
            <h4 className="text-base font-semibold text-gray-900 m-0">
              Allocation Charts
            </h4>
            <RadioGroup value={chartType} onChange={setChartType}>
              <div className="flex gap-2">
                <RadioGroup.Option value="pie">
                  {({ checked }) => (
                    <div className={`flex items-center gap-2 cursor-pointer px-3 py-2 rounded-lg border transition-colors ${
                      checked ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    }`}>
                      <ChartPieIcon className="w-4 h-4" />
                      <span className="text-sm font-medium">Pie</span>
                    </div>
                  )}
                </RadioGroup.Option>
                <RadioGroup.Option value="bar">
                  {({ checked }) => (
                    <div className={`flex items-center gap-2 cursor-pointer px-3 py-2 rounded-lg border transition-colors ${
                      checked ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    }`}>
                      <ChartBarIcon className="w-4 h-4" />
                      <span className="text-sm font-medium">Bar</span>
                    </div>
                  )}
                </RadioGroup.Option>
              </div>
            </RadioGroup>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {portfolios.map(portfolio => {
              const chartData = getChartDataForPortfolio(portfolio.id);
              if (chartData.length === 0) return null;

              return (
                <div
                  key={portfolio.id}
                  className="flex flex-col p-4 border border-gray-200 rounded-lg bg-white shadow-sm cursor-pointer hover:shadow-md hover:border-blue-300 transition-all"
                  onClick={() => navigate(`/portfolio/${portfolio.id}`)}
                >
                  <div className="text-center mb-3 pb-3 border-b border-gray-200">
                    <div className="text-sm font-semibold text-gray-900">{portfolio.name}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {formatCurrency(portfolio.currentValue, portfolio.currency, 0)}
                    </div>
                  </div>

                  {chartType === 'pie' ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={chartData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => name ? `${name} ${(percent * 100).toFixed(0)}%` : `${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                          isAnimationActive={false}
                        >
                          {chartData.map((entry, index) => (
                            <RechartsCell key={`cell-${index}`} fill={entry.color || FINANCIAL_COLORS[index % FINANCIAL_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value) => formatCurrency(value, portfolio.currency, 0)}
                        />
                        <Legend
                          iconType="circle"
                          formatter={(value) => value}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={chartData}>
                        <XAxis
                          dataKey="name"
                          angle={-45}
                          textAnchor="end"
                          height={80}
                          tick={{ fontSize: 11 }}
                        />
                        <YAxis
                          tick={{ fontSize: 11 }}
                          tickFormatter={(value) => formatCurrency(value, portfolio.currency, 0)}
                        />
                        <Tooltip
                          formatter={(value) => formatCurrency(value, portfolio.currency, 0)}
                        />
                        <Legend
                          formatter={(value) => value}
                        />
                        <Bar dataKey="value" name="Allocated Value" isAnimationActive={false}>
                          {chartData.map((entry, index) => (
                            <RechartsCell key={`cell-${index}`} fill={entry.color || FINANCIAL_COLORS[index % FINANCIAL_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              );
            })}
          </div>
        </div>
          )}
        </>
      )}
    </div>
  );
};

export default Allocations;
